import { defineComponent as _defineComponent } from 'vue'
import BaseCountdownTimer from 'JS/gifts/components/countdown/BaseCountdownTimer.vue';
import Icon from 'JS/components/Icon.vue';
import VIntersectionObserver from 'JS/components/VIntersectionObserver.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { Countdown, getCountdown } from 'JS/utilities/simple-countdown';
import { createPromoDataAttributes } from 'JS/utilities/analytics';
import { HeaderAdCountdown, IHeaderAd } from 'Src/js/types/Header';
import { usePrefersReducedMotion } from 'JS/composables/prefers-reduced-motion';


export default /*#__PURE__*/_defineComponent({
  __name: 'HeaderAdsBanner',
  props: {
    ads: null
  },
  setup(__props: any) {

const props = __props as {
	ads: IHeaderAd[];
}

const TRANSITION_TIME = 750;
const AUTO_SCROLL_PAUSE_TIME = 7000;



const trackId = "header-ads-banner-track";
const currentIndex = ref<number>(0);
const totalAds = computed(() => props.ads?.length ?? 0);
const isCarousel = computed(() => totalAds.value > 1);

const renderedAds = computed(()=> {
	if (!isCarousel.value) {
		return props.ads;
	}

	return [
		props.ads[props.ads.length - 1],
		...props.ads,
		props.ads[0]
	];
});

const ariaLiveText = computed(() => {
	const currentAd = renderedAds.value[currentIndex.value + 1];
	if (isAutoScrolling.value || !currentAd) {
		return null;
	}

	let countdownText = "";

	// NOTE: the timer here will not be updated live. This is intentional; it avoids
	// spamming screen-reader users with announcements every second.
	if (currentAd.countdown !== HeaderAdCountdown.None) {
		const countdown = getCountdown({
			from: Date.now(),
			to: new Date(currentAd.endDateTime)
		});

		countdownText = createCountdownA11yLabel(countdown);
	}

	return `Showing slide ${createSlidePositionLabel(currentIndex.value + 1)}: ${currentAd.text}. ${countdownText}`;
});

function createSlidePositionLabel(index: number): string | undefined {
	if (totalAds.value === 1) {
		return undefined;
	}

	// makes index 1-indexed, for user facing count and
	// makes the copies on either end match the originals
	if (index === 0) {
		index = totalAds.value;
	} else if (index > totalAds.value) {
		index = 1;
	}

	return `${index} of ${totalAds.value}`;
}


function createCountdownLabel(ad: IHeaderAd, timerProps: Countdown) {
	const countdown = formatCountdown(timerProps);
	if (ad.countdown === HeaderAdCountdown.Before) {
		return `${countdown} ${ad.text}`;
	}

	return `${ad.text} ${countdown}`;
}

function formatCountdown({ days, hours, minutes, seconds }: Countdown) {
	let hourString = hours.toString().padStart(2, '0');
	const minuteString = minutes.toString().padStart(2, '0');
	const secondString = seconds.toString().padStart(2, '0');

	if (days <= 1) {
		hourString = (hours + (days * 24)).toString().padStart(2, '0');
		return `${hourString}:${minuteString}:${secondString}`;
	} else {
		const dayString = days.toString().padStart(2, '0');

		return `${dayString}:${hourString}:${minuteString}:${secondString}`;
	}
}

function createCountdownA11yLabel({ days, hours, minutes, seconds }: Countdown) {
	let dayString = "";
	let hourString = "";
	let minuteString = "";
	let secondString = "";

	if (days !== 0) {
		dayString = `${days} Day${days === 1 ? '' : 's'}, `;
	}

	if (hours !== 0) {
		hourString = `${hours} Hour${hours === 1 ? '' : 's'}, `;
	}

	if (minutes !== 0) {
		minuteString = `${minutes} Minute${minutes === 1 ? '' : 's'}, `;
	}

	if (seconds !== 0) {
		secondString = `${seconds} Second${seconds === 1 ? '' : 's'}`;
	}

	return dayString + hourString + minuteString + secondString + ' left.';
}

// Scrolling

function handleArrow(direction: 'left' | 'right') {
	disableAutoScroll();
	if (direction === 'left') {
		prev();
	} else {
		next();
	}
}

function prev() {
	if (currentIndex.value <= -1) return; // prevent blanks on fast prev-click
	currentIndex.value -= 1;
	scroll();
}

function next() {
	if (currentIndex.value >= totalAds.value) return; // prevent blanks on fast next-click
	currentIndex.value += 1;
	scroll();
}

const prefersReducedMotion = usePrefersReducedMotion();
const carouselTrack = ref<HTMLElement | null>(null);
function scroll(ms: number = TRANSITION_TIME) {
	const isSlideAction = ms > 0;
	if (prefersReducedMotion.value) {
		ms = 0;
	}

	// Move slider
	carouselTrack.value.style.transitionDuration = `${ms}ms`;
	carouselTrack.value.style.transform = `translate3D(${(-currentIndex.value - 1) * 100}%, 0, 0)`;

	// the transitionend event doesn't fire when we force ms to be 0
	// this ensures we end up at the right position when wrapping
	if (isSlideAction && prefersReducedMotion.value) {
		correctWrappedIndex();
	}
}

function handleTransitionEnd() {
	correctWrappedIndex();
}

function correctWrappedIndex() {
	if (currentIndex.value <= -1) {
		currentIndex.value = totalAds.value - 1;
		scroll(0);
	} else if (currentIndex.value >= totalAds.value) {
		currentIndex.value = 0;
		scroll(0);
	}
}

// Auto-Scrolling

const isAutoScrolling = ref<boolean>(false);
const isAutoScrollDisabled = ref(false);
const isAutoScrollAllowed = computed(() => {
	return isCarousel.value && !isAutoScrollDisabled.value && !prefersReducedMotion.value;
});

let autoScrollInterval: number | undefined;
function startAutoScroll() {
	if (!isAutoScrollAllowed.value) {
		return;
	}

	stopAutoScroll(); // make sure to clear existing interval if running

	isAutoScrolling.value = true;
	autoScrollInterval = window.setInterval(next, TRANSITION_TIME + AUTO_SCROLL_PAUSE_TIME);
}

function stopAutoScroll() {
	isAutoScrolling.value = false;
	window.clearInterval(autoScrollInterval);
};

function disableAutoScroll() {
	isAutoScrollDisabled.value = true;
	stopAutoScroll();
}

const hasPointer = ref(false);
const hasFocus = ref(false);

// ensures that autoplay is paused when focus/mouse within container
watch(() => hasPointer.value || hasFocus.value, pause => {
	if (pause) {
		stopAutoScroll();
	} else {
		startAutoScroll();
	}
});

// if no longer allowed, make sure to stop auto scrolling
watch(isAutoScrollAllowed, allowed => {
	if (!allowed) {
		stopAutoScroll();
	}
});

function handleFocusIn(e: FocusEvent) {
	if ((e.currentTarget as HTMLElement).contains(e.target as Node)) {
		hasFocus.value = true;
	};
}

function handleFocusOut(e: FocusEvent) {
	if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
		hasFocus.value = false;
	};
}

// NOTE: this component is not properly reactive to changes to the `ads` prop.
// At time of writing, `ads` doesn't change, so this is okay. Beware if that changes.
onMounted(()=> {
	if (isAutoScrollAllowed.value) {
		startAutoScroll();
	}

	if (isCarousel.value) {
		currentIndex.value = 0;
		scroll(0);
	}
});

//GA TRACKING START
const viewport = ref<InstanceType<typeof VIntersectionObserver> | null>(null);
const viewportObserverOptions = { rootMargin: '-15px' };
// intersections are triggered relative to the banner viewport element, via the `root` option,
// instead of the browser viewport
const singleAdObserverOptions = computed(()=> {
	return { root: viewport.value?.$el };
});

const isViewportOnScreen = ref<boolean>();

function handleViewportEnter() {
	isViewportOnScreen.value = true;
	trackView(renderedAds.value[currentIndex.value + 1], currentIndex.value + 1);
}

function handleViewportLeave() {
	isViewportOnScreen.value = false;
}

function shouldHaveGaTracking(ad: IHeaderAd) {
	return !!(ad?.link ?? false);
}

function getGaDataAttributes(ad: IHeaderAd, idx: number) {
	if (!shouldHaveGaTracking(ad)) {
		return undefined;
	}

	return {
		'data-ga-link': '',
		...createPromoDataAttributes({
			promoId: ad.link.replace("https://www.farmandfleet.com", ""),
			creativeSlot: `scrolling_messages_${normalizeIndex(idx)}`,
			// TODO: this is different from creative_name in trackView. intentional?
			creativeName: "scrolling_messages",
			promoName: `${ad.text}`,
		}),
	};
};

const alreadyTracked = new Set<number>();
function trackView(ad: IHeaderAd, idx: number) {
	idx = normalizeIndex(idx);

	const shouldTrackView = isViewportOnScreen.value && shouldHaveGaTracking(ad) && !alreadyTracked.has(idx);
	if (!shouldTrackView) {
		return;
	}

	//Copied from the View Promotion Tag in GTM
	window.blainGtmDataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
	window.blainGtmDataLayer.push({
		'event': 'view_promotion',
		'ecommerce': {
			'items': [
				{
					'promotion_id': ad.link.replace("https://www.farmandfleet.com", ""),
					'promotion_name': `${ad.text}`,
					'creative_name': "scrolling_messages" + "_" + window.location.pathname,
					'creative_slot': `scrolling_messages_${idx}`,
				},
			]
		}
	});

	alreadyTracked.add(idx);
}

function normalizeIndex(idx: number) {
	if (totalAds.value < 1) {
		return idx;
	}

	if (totalAds.value === 1) {
		return 1;
	}

	if (idx === 0) {
		return renderedAds.value.length - 2;
	}

	if (idx === renderedAds.value.length - 1) {
		return 1;
	}

	return idx;
}
//GA TRACKING END

return { __sfc: true,TRANSITION_TIME, AUTO_SCROLL_PAUSE_TIME, props, trackId, currentIndex, totalAds, isCarousel, renderedAds, ariaLiveText, createSlidePositionLabel, createCountdownLabel, formatCountdown, createCountdownA11yLabel, handleArrow, prev, next, prefersReducedMotion, carouselTrack, scroll, handleTransitionEnd, correctWrappedIndex, isAutoScrolling, isAutoScrollDisabled, isAutoScrollAllowed, autoScrollInterval, startAutoScroll, stopAutoScroll, disableAutoScroll, hasPointer, hasFocus, handleFocusIn, handleFocusOut, viewport, viewportObserverOptions, singleAdObserverOptions, isViewportOnScreen, handleViewportEnter, handleViewportLeave, shouldHaveGaTracking, getGaDataAttributes, alreadyTracked, trackView, normalizeIndex, BaseCountdownTimer, Icon, VIntersectionObserver, HeaderAdCountdown }
}

})